import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Camera, MapPin, X, Loader2, RefreshCw, Clock, Monitor, Smartphone, Tablet,
  CheckCircle2, CalendarCheck, ExternalLink, AlertTriangle, LogOut, Timer,
} from 'lucide-react';
import { api } from '@/api/client';

function detectDevice(): string {
  const ua = navigator.userAgent;
  if (/\b(iPad|Tablet)\b/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return 'Tablet';
  if (/Mobi|iPhone|iPod|Android.*Mobile|Windows Phone/i.test(ua)) return 'Mobile';
  return 'Desktop';
}

/** Best-effort reverse geocode (free, no key — same service the Scan form uses). */
async function reverseGeocode(lat: number, lng: number): Promise<{ city: string; area: string; state: string }> {
  try {
    const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`);
    const j = await r.json();
    const admin = Array.isArray(j.localityInfo?.administrative) ? j.localityInfo.administrative : [];
    const finest = admin.length ? admin[admin.length - 1]?.name : '';
    return {
      city: j.city || j.locality || j.principalSubdivision || '',
      area: j.locality || finest || '',
      state: j.principalSubdivision || '',
    };
  } catch { return { city: '', area: '', state: '' }; }
}

function DeviceIcon({ type, size = 14 }: { type?: string; size?: number }) {
  if (type === 'Mobile') return <Smartphone size={size} />;
  if (type === 'Tablet') return <Tablet size={size} />;
  return <Monitor size={size} />;
}

function fmt(dt: string) {
  try { return new Date(dt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return dt; }
}
function fmtTime(dt?: string | null) {
  if (!dt) return '—';
  try { return new Date(dt).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' }); }
  catch { return dt; }
}

/**
 * Header button. Shown to every internal (admin-portal) user. If the user's mobile
 * matches an employee they get the mark/history modal; otherwise a friendly
 * "not found — contact HR" message.
 */
export function MarkAttendanceButton() {
  const { data: link, isLoading, isError, refetch } = useQuery({
    queryKey: ['attendance-link'],
    queryFn: async () => (await api.get('/me/attendance-link')).data as { linked: boolean; employeeName?: string },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  // Today's in/out state drives what the button prompts (Check In vs Check Out vs done).
  const { data: today } = useQuery({
    queryKey: ['attendance-today'],
    queryFn: async () => (await api.get('/me/attendance-today')).data as TodayStatus,
    enabled: !!link?.linked,
    staleTime: 60 * 1000,
  });
  const [open, setOpen] = useState(false);

  const phase: 'checkin' | 'checkout' | 'done' | 'na' =
    !link?.linked ? 'na'
    : today?.markedOut ? 'done'
    : today?.markedIn ? 'checkout'
    : 'checkin';
  const label = phase === 'checkout' ? 'Check Out' : phase === 'done' ? 'Attendance done' : 'Check In';
  const Icon = phase === 'checkout' ? LogOut : phase === 'done' ? CheckCircle2 : CalendarCheck;
  const color = phase === 'checkout'
    ? 'bg-amber-500 hover:bg-amber-600'
    : phase === 'done'
      ? 'bg-slate-500 hover:bg-slate-600'
      : 'bg-emerald-600 hover:bg-emerald-700';
  const initialTab: 'mark' | 'out' | 'history' = phase === 'checkout' ? 'out' : phase === 'done' ? 'history' : 'mark';

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title={phase === 'checkout' ? 'Check out — going out of office' : 'Mark your attendance'}
        className={`flex items-center gap-1.5 px-3 py-1.5 ${color} rounded-lg text-white text-xs font-medium transition shadow-sm`}>
        <Icon size={14} /> <span className="hidden sm:inline">{label}</span>
      </button>
      {open && (link?.linked
        ? <AttendanceModal onClose={() => setOpen(false)} employeeName={link.employeeName} initialTab={initialTab} />
        : <NotLinkedModal onClose={() => setOpen(false)} loading={isLoading} isError={isError} onRetry={() => refetch()} />)}
    </>
  );
}

function NotLinkedModal({ onClose, loading, isError, onRetry }: { onClose: () => void; loading: boolean; isError?: boolean; onRetry?: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 text-center" onClick={(e) => e.stopPropagation()}>
        {loading ? (
          <div className="py-6"><Loader2 className="animate-spin mx-auto text-slate-400" /></div>
        ) : isError ? (
          <>
            <div className="mx-auto w-12 h-12 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mb-3">
              <AlertTriangle className="text-red-500" size={22} />
            </div>
            <h3 className="font-semibold text-slate-900">Couldn't check your attendance access</h3>
            <p className="text-sm text-slate-500 mt-1">
              We couldn't reach the server to verify your employee link. Please check your connection and try again.
            </p>
            <div className="mt-4 flex gap-2 justify-center">
              <button onClick={() => onRetry?.()} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center gap-1"><RefreshCw size={14} /> Try again</button>
              <button onClick={onClose} className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">Close</button>
            </div>
          </>
        ) : (
          <>
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center mb-3">
              <AlertTriangle className="text-amber-500" size={22} />
            </div>
            <h3 className="font-semibold text-slate-900">Mobile number not found</h3>
            <p className="text-sm text-slate-500 mt-1">
              Your mobile number is not found in our employee records. Please contact HR to get it added to your
              employee profile — then you'll be able to mark attendance.
            </p>
            <button onClick={onClose} className="mt-4 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800">Got it</button>
          </>
        )}
      </div>
    </div>
  );
}

type Coords = { latitude: number; longitude: number; accuracy?: number };
type Geo = { city: string; area: string; state: string };
type TodayStatus = { linked: boolean; markedIn?: boolean; inAt?: string | null; markedOut?: boolean; outAt?: string | null; hoursWorked?: number | null };

function AttendanceModal({ onClose, employeeName, initialTab = 'mark' }: { onClose: () => void; employeeName?: string; initialTab?: 'mark' | 'out' | 'history' }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'mark' | 'out' | 'history'>(initialTab);
  const [photo, setPhoto] = useState<string | null>(null);
  const [coords, setCoords] = useState<Coords | null>(null);
  const [geo, setGeo] = useState<Geo | null>(null);
  const [geoState, setGeoState] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const [geoError, setGeoError] = useState<string>('');
  const [camError, setCamError] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const { data: today } = useQuery({
    queryKey: ['attendance-today'],
    queryFn: async () => (await api.get('/me/attendance-today')).data as TodayStatus,
    staleTime: 0,
  });

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const startCamera = useCallback(async () => {
    setCamError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
    } catch (e: any) {
      setCamError('Camera unavailable or blocked. Allow camera access, or use "Upload photo" below.');
    }
  }, []);

  const requestLocation = useCallback(() => {
    if (!('geolocation' in navigator)) { setGeoState('error'); setGeoError('This device does not support location.'); return; }
    setGeoState('loading'); setGeoError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setCoords({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGeoState('ok');
        setGeo(await reverseGeocode(pos.coords.latitude, pos.coords.longitude));
      },
      (err) => {
        setGeoState('error');
        setGeoError(err.code === 1
          ? 'Location permission denied. You must allow location to mark attendance — enable it and retry.'
          : 'Could not get your location. Move to an open area and retry.');
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  }, []);

  // Request location once; start the camera only on the mark-in tab (until a photo is taken).
  useEffect(() => {
    if (tab === 'history') { stopCamera(); return; }
    if (geoState === 'idle') requestLocation();
    if (tab === 'mark' && !photo) startCamera(); else stopCamera();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, photo]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const capture = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return;
    const w = 480, h = Math.round((v.videoHeight / v.videoWidth) * 480);
    c.width = w; c.height = h;
    c.getContext('2d')!.drawImage(v, 0, 0, w, h);
    setPhoto(c.toDataURL('image/jpeg', 0.6));
    stopCamera();
  };

  const onUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const c = canvasRef.current!; const w = 480, h = Math.round((img.height / img.width) * 480);
        c.width = w; c.height = h; c.getContext('2d')!.drawImage(img, 0, 0, w, h);
        setPhoto(c.toDataURL('image/jpeg', 0.6));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(f);
  };

  const refreshAfterMark = () => {
    qc.invalidateQueries({ queryKey: ['my-attendance'] });
    qc.invalidateQueries({ queryKey: ['attendance-today'] });
  };

  const save = useMutation({
    mutationFn: async () => (await api.post('/me/attendance', {
      latitude: coords?.latitude, longitude: coords?.longitude, accuracy: coords?.accuracy,
      selfie: photo, deviceType: detectDevice(),
      city: geo?.city, area: geo?.area, state: geo?.state,
    })).data,
    onSuccess: () => {
      toast.success('Attendance marked');
      refreshAfterMark();
      setPhoto(null); setTab('history');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not mark attendance'),
  });

  const markOut = useMutation({
    mutationFn: async () => (await api.post('/me/attendance-out', {
      latitude: coords?.latitude, longitude: coords?.longitude, accuracy: coords?.accuracy,
      deviceType: detectDevice(),
      city: geo?.city, area: geo?.area, state: geo?.state,
    })).data as { hoursWorked: number },
    onSuccess: (d) => {
      toast.success(`Marked out — ${d.hoursWorked} hour${d.hoursWorked === 1 ? '' : 's'} worked today`);
      refreshAfterMark();
      setTab('history');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not mark out'),
  });

  const canSubmitIn = !!photo && !!coords && !save.isPending;
  const canSubmitOut = !!coords && !markOut.isPending;

  const LocationBar = (
    <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${geoState === 'ok' ? 'border-emerald-200 bg-emerald-50' : geoState === 'error' ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
      <MapPin size={16} className="mt-0.5 shrink-0" />
      <div className="flex-1">
        {geoState === 'loading' && <span className="text-slate-600">Getting your location…</span>}
        {geoState === 'ok' && coords && (
          <span className="text-emerald-700">
            Location captured — {coords.latitude.toFixed(5)}, {coords.longitude.toFixed(5)}{coords.accuracy ? ` (±${Math.round(coords.accuracy)}m)` : ''}
            {geo && (geo.city || geo.area || geo.state) && (
              <span className="block text-emerald-600/80 text-xs mt-0.5">{[geo.area, geo.city, geo.state].filter(Boolean).join(', ')}</span>
            )}
          </span>
        )}
        {geoState === 'error' && <span className="text-red-700">{geoError}</span>}
        {geoState === 'idle' && <span className="text-slate-600">Location required.</span>}
      </div>
      {geoState !== 'ok' && (
        <button onClick={requestLocation} className="text-xs px-2 py-1 bg-white border border-slate-300 rounded hover:bg-slate-50 flex items-center gap-1">
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-900">Attendance</h3>
            {employeeName && <p className="text-xs text-slate-500">{employeeName}</p>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        {/* Today summary */}
        {today?.linked && (today.markedIn || today.markedOut) && (
          <div className="mx-5 mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <span className="flex items-center gap-1 text-slate-600"><Clock size={12} /> In: <b className="text-slate-900">{fmtTime(today.inAt)}</b></span>
            <span className="flex items-center gap-1 text-slate-600"><LogOut size={12} /> Out: <b className="text-slate-900">{fmtTime(today.outAt)}</b></span>
            {today.hoursWorked != null && <span className="flex items-center gap-1 text-emerald-700"><Timer size={12} /> <b>{today.hoursWorked}h</b> worked</span>}
          </div>
        )}

        <div className="flex gap-1 px-5 pt-3">
          {([['mark', 'Check In'], ['out', 'Check Out'], ['history', 'History']] as const).map(([t, lbl]) => {
            const dot = t === 'mark' && today?.markedIn ? 'in' : t === 'out' && today?.markedOut ? 'out' : null;
            return (
              <button key={t} onClick={() => setTab(t)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 ${tab === t ? 'bg-emerald-50 text-emerald-700' : 'text-slate-500 hover:bg-slate-100'}`}>
                {lbl}
                {dot && <CheckCircle2 size={13} className="text-emerald-500" />}
              </button>
            );
          })}
        </div>

        <div className="p-5 overflow-y-auto">
          {tab === 'mark' ? (
            <div className="space-y-4">
              {today?.markedIn && !today?.markedOut && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex items-center justify-between gap-2">
                  <span>You checked in at <b>{fmtTime(today.inAt)}</b>. Leaving office?</span>
                  <button onClick={() => setTab('out')} className="px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 flex items-center gap-1 shrink-0">
                    <LogOut size={13} /> Check Out
                  </button>
                </div>
              )}
              {today?.markedOut && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  You've completed attendance for today (in {fmtTime(today.inAt)} · out {fmtTime(today.outAt)}{today.hoursWorked != null ? ` · ${today.hoursWorked}h` : ''}).
                </div>
              )}
              {LocationBar}

              {/* Selfie */}
              <div className="rounded-lg border border-slate-200 overflow-hidden bg-slate-900 aspect-[4/3] flex items-center justify-center relative">
                {photo ? (
                  <img src={photo} alt="selfie" className="w-full h-full object-contain" />
                ) : (
                  <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
                )}
                {!photo && camError && (
                  <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-xs text-white/90 bg-black/50">
                    <div><AlertTriangle size={20} className="mx-auto mb-1 text-amber-400" />{camError}</div>
                  </div>
                )}
              </div>
              <canvas ref={canvasRef} className="hidden" />

              <div className="flex items-center gap-2">
                {!photo ? (
                  <>
                    <button onClick={capture} disabled={!!camError}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
                      <Camera size={16} /> Capture selfie
                    </button>
                    <label className="px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 cursor-pointer">
                      Upload photo
                      <input type="file" accept="image/*" capture="user" onChange={onUpload} className="hidden" />
                    </label>
                  </>
                ) : (
                  <button onClick={() => setPhoto(null)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-1">
                    <RefreshCw size={14} /> Retake
                  </button>
                )}
              </div>

              <button onClick={() => save.mutate()} disabled={!canSubmitIn}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition disabled:opacity-50">
                {save.isPending ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                Check In
              </button>
              {!canSubmitIn && !save.isPending && (
                <p className="text-xs text-slate-400 text-center">Capture a selfie and allow location to check in.</p>
              )}
            </div>
          ) : tab === 'out' ? (
            <div className="space-y-4">
              {today?.markedOut ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-6 text-center">
                  <CheckCircle2 className="mx-auto text-emerald-600 mb-2" size={28} />
                  <p className="text-sm text-emerald-800 font-medium">You've already marked out today.</p>
                  <p className="text-xs text-emerald-700 mt-1">Out at {fmtTime(today.outAt)} · {today.hoursWorked ?? '—'}h worked</p>
                </div>
              ) : (
                !today?.markedIn ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center">
                    <AlertTriangle className="mx-auto text-amber-500 mb-2" size={26} />
                    <p className="text-sm text-amber-800 font-medium">You haven't checked in today.</p>
                    <p className="text-xs text-amber-700 mt-1">Check in first — then you can check out when leaving.</p>
                    <button onClick={() => setTab('mark')} className="mt-3 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 inline-flex items-center gap-1">
                      <CalendarCheck size={14} /> Go to Check In
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                      Check out captures your <b>location &amp; device</b> and calculates today's working hours from your check-in time.
                      <span className="block text-xs text-slate-500 mt-1">Checked in at <b>{fmtTime(today.inAt)}</b>.</span>
                    </div>
                    {LocationBar}
                    <button onClick={() => markOut.mutate()} disabled={!canSubmitOut}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition disabled:opacity-50">
                      {markOut.isPending ? <Loader2 size={16} className="animate-spin" /> : <LogOut size={16} />}
                      Check Out
                    </button>
                    {!canSubmitOut && !markOut.isPending && (
                      <p className="text-xs text-slate-400 text-center">Allow location to check out.</p>
                    )}
                  </>
                )
              )}
            </div>
          ) : (
            <HistoryList />
          )}
        </div>
      </div>
    </div>
  );
}

function HistoryList() {
  const { data, isLoading } = useQuery({
    queryKey: ['my-attendance'],
    queryFn: async () => (await api.get('/me/attendance', { params: { limit: 100 } })).data as { rows: any[] },
  });
  const [zoom, setZoom] = useState<string | null>(null);
  if (isLoading) return <div className="py-10 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>;
  const rows = data?.rows ?? [];
  if (rows.length === 0) return <div className="py-10 text-center text-slate-400 text-sm">No attendance marked yet.</div>;
  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const isOut = r.kind === 'OUT';
        return (
          <div key={r.id} className="flex items-center gap-3 border border-slate-100 rounded-lg p-2">
            {r.selfieUrl
              ? <img src={r.selfieUrl} alt="" onClick={() => setZoom(r.selfieUrl)} className="w-12 h-12 rounded object-cover cursor-pointer shrink-0" />
              : <div className="w-12 h-12 rounded bg-slate-100 shrink-0 flex items-center justify-center text-slate-400"><LogOut size={16} /></div>}
            <div className="flex-1 min-w-0 text-sm">
              <div className="flex items-center gap-1.5 text-slate-900 font-medium">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${isOut ? 'bg-slate-200 text-slate-700' : 'bg-emerald-100 text-emerald-700'}`}>{isOut ? 'OUT' : 'IN'}</span>
                <Clock size={13} className="text-slate-400" /> {fmt(r.markedAt)}
                {isOut && r.hoursWorked != null && <span className="text-xs text-emerald-700 font-semibold">· {r.hoursWorked}h</span>}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500 mt-0.5">
                <span className="flex items-center gap-1"><DeviceIcon type={r.deviceType} /> {r.deviceType} · {r.os}</span>
                {r.ipAddress && <span>IP {r.ipAddress}</span>}
                {(r.city || r.state) && <span>{[r.area, r.city, r.state].filter(Boolean).join(', ')}</span>}
                {r.latitude != null && r.longitude != null && (
                  <a href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 text-emerald-600 hover:underline">
                    <MapPin size={12} /> {Number(r.latitude).toFixed(4)}, {Number(r.longitude).toFixed(4)} <ExternalLink size={10} />
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {zoom && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-6" onClick={() => setZoom(null)}>
          <img src={zoom} alt="selfie" className="max-h-[80vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
}
